import React from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Award, Building2, Boxes, Globe, Users, Clock, ArrowRight } from "lucide-react";

const STUDIO_IMG = "https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const OFFICE_IMG = "https://images.pexels.com/photos/7031622/pexels-photo-7031622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const About = () => {
  React.useEffect(() => {
    document.title = "About — Dream Home Engineers | Architectural Design Since 2012";
  }, []);

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="overline text-accent">About Dream Home Engineers</div>
          <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95] tracking-tighter">
            Thirteen years.<br />Forty countries.<br /><span className="italic text-accent">One studio.</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-lg">
            Founded in 2012 in Hyderabad, India, Dream Home Engineers is a full-service architectural design studio offering house plans, BIM, CAD drafting, 3D modeling, and photorealistic rendering to homeowners, developers, and design firms across 40+ countries.
          </p>
        </div>
        <div className="lg:col-span-6">
          <div className="aspect-[4/5] overflow-hidden bg-muted">
            <img src={STUDIO_IMG} alt="Dream Home Engineers studio" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="overline text-accent-foreground/70">Our Practice</div>
          <p className="font-display text-3xl lg:text-4xl mt-6 leading-snug">
            "We built Dream Home Engineers to close the gap between generic stock plans and unaffordable custom architecture — delivering permit-ready, buildable, beautiful plans that ordinary homeowners can actually afford, adapt, and build."
          </p>
          <div className="mt-8">
            <div className="font-semibold text-lg">B. Ch. S. R. Nagesh</div>
            <div className="text-sm text-primary-foreground/70">Founder & Managing Partner</div>
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            { icon: Award, big: "2012", sub: "Established" },
            { icon: Building2, big: "Curated", sub: "House Plans" },
            { icon: Users, big: "25+", sub: "Architects & Designers" },
            { icon: Globe, big: "40+", sub: "Countries Served" },
          ].map(({ icon: Icon, big, sub }) => (
            <div key={sub}>
              <Icon size={22} className="text-accent" />
              <div className="font-display text-5xl mt-4 leading-none">{big}</div>
              <div className="overline text-muted-foreground mt-3">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services scope */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="aspect-square overflow-hidden bg-muted">
            <img src={OFFICE_IMG} alt="Architectural drafting at Dream Home Engineers" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="overline text-accent">What we do</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">Complete architectural services under one roof.</h2>
          <p className="mt-6 text-muted-foreground text-lg">
            Beyond our curated library of ready-to-build house plans, our 25-person team offers full architectural design, BIM modeling in Revit (LOD 100–500), AutoCAD-certified drafting, 3D modeling, and photorealistic rendering for interior and exterior visualization.
          </p>
          <ul className="mt-8 space-y-4 text-lg">
            <li className="flex gap-4"><span className="text-accent overline mt-1">01</span><span>Architectural design (residential, commercial, mixed-use)</span></li>
            <li className="flex gap-4"><span className="text-accent overline mt-1">02</span><span>BIM & Revit modeling with clash detection</span></li>
            <li className="flex gap-4"><span className="text-accent overline mt-1">03</span><span>CAD drafting & construction documents</span></li>
            <li className="flex gap-4"><span className="text-accent overline mt-1">04</span><span>3D modeling & photorealistic rendering</span></li>
            <li className="flex gap-4"><span className="text-accent overline mt-1">05</span><span>Design consulting & plan review</span></li>
          </ul>
          <div className="mt-10 flex gap-3 flex-wrap">
            <Link to="/services">
              <Button size="lg" className="rounded-none bg-primary hover:bg-primary/90 h-12 px-8">
                Explore services <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="rounded-none h-12 px-8 border-foreground">
                Talk to our team
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Office info */}
      <section className="bg-muted/40 border-y border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="overline text-accent">Our Studio</div>
            <div className="font-display text-2xl mt-3 leading-tight">Dream Home Engineers</div>
            <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
              21-205, Valli Nilayam<br />
              Balajinagar, Kukatpally<br />
              Hyderabad, Telangana 500072<br />
              India
            </div>
          </div>
          <div>
            <div className="overline text-accent">Reach the founder</div>
            <div className="mt-3 text-sm text-muted-foreground">
              <div>B. Ch. S. R. Nagesh</div>
              <div>Founder & Managing Partner</div>
              <a href="mailto:nagesh@dreamhomeengineers.com" className="block mt-3 text-foreground hover:text-accent transition-fast">nagesh@dreamhomeengineers.com</a>
              <a href="tel:+919550899555" className="block text-foreground hover:text-accent transition-fast">+91 95508 99555</a>
              <a href="tel:+914048502772" className="block text-foreground hover:text-accent transition-fast">+91 40 4850 2772 (Landline)</a>
            </div>
          </div>
          <div>
            <div className="overline text-accent">Parent site</div>
            <div className="mt-3 text-sm text-muted-foreground">
              For our full portfolio of architectural, BIM and design services, visit our parent company site:
            </div>
            <a href="https://dreamhomeengineers.com" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-foreground hover:text-accent transition-fast">
              dreamhomeengineers.com <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline text-accent">A brief history</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight tracking-tight">
              Thirteen years, in five milestones.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <ol className="space-y-8">
              {[
                { year: "2012", title: "Studio founded", note: "Dream Home Engineers established in Hyderabad, India — starting with residential architectural design for local clients." },
                { year: "2015", title: "International expansion", note: "First project delivered to the USA; over the next three years, we expanded to serve the UK, Canada, Australia, UAE, and Singapore." },
                { year: "2018", title: "BIM services launched", note: "Added a dedicated Revit / BIM team offering LOD 300–500 modeling and clash detection for builders and MEP consultants worldwide." },
                { year: "2021", title: "Plan library at 3,000+", note: "Our curated stock plan library crossed 3,000 house plans covering Modern, Traditional, Farmhouse, Duplex, Luxury Villa and Small House typologies." },
                { year: "2026", title: "plans.dreamhomeengineers.com launched", note: "Today: a growing curated library, 25+ architects and designers, and clients across 40+ countries — with our brand-new marketplace open to homeowners and builders worldwide." },
              ].map((m) => (
                <li key={m.year} className="grid grid-cols-[80px_1fr] gap-6 items-start border-b border-border pb-8">
                  <div className="font-display text-3xl text-accent">{m.year}</div>
                  <div>
                    <div className="font-display text-2xl">{m.title}</div>
                    <div className="mt-2 text-muted-foreground">{m.note}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
